import {
    Checkbox, DefaultButton, Dropdown, IDropdownOption,
    IPanelStyles, Panel, PanelType, PrimaryButton
} from "@fluentui/react";
import { DataUtils } from "../../../common/views/DataUtils";
import { checkboxStyles, defaultButtonStyles, dropdownStyles, primaryButtonStyles } from "../../../common/views/DefaultStyles";
import FilterButton from "../../../common/views/FilterButton";
import { Utils } from "../../../common/views/Utils";
import { SortModel } from "../../../home/models/PageTypes";
import { MenuModel } from "../../models/CategoryModel";
import { ProductFilterModel } from "../../models/ProductModel";
import { ProductUtils } from "../../ProductUtils";
import CategoryMenuItemView from "../shared/CategoryMenuItemView";

const panelStyles: Partial<IPanelStyles> = {
    header: {
        position: 'absolute', // Make the header absolute
        top: 0,
        left: 0,
        width: '100%', // Ensure it stretches to the full width
        backgroundColor: 'white', // Optional: Set a background to cover content
        zIndex: 1, // Ensure it's on top of other elements
        padding: '16px 24px', // Add padding to match default styles
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', // Optional shadow for separation
    },
    content: {
        marginTop: '64px', // Push content down to account for the header height
    },
};

export type ProductFilterProps = {
    filter: ProductFilterModel;
    onValueChange: (field: keyof ProductFilterModel, value: any) => void;
    categoryList: MenuModel[];
    getSelectedCategoryList: () => MenuModel[];
    onCategoryChecked: (category: MenuModel, checked: boolean) => void;
    onCategoryCollapsed: (category: MenuModel, collapsed: boolean) => void;
    sizeOptions: IDropdownOption[];
    onCancel: () => void;
    onFilter: () => void;
}

export default function ProductFilterView({
    filter, onValueChange, categoryList, getSelectedCategoryList, onCategoryChecked,
    onCategoryCollapsed, sizeOptions, onFilter, onCancel
}: ProductFilterProps) {
    const sortOptions: SortModel[] = ProductUtils.getSortOptions();
    const featureOptions: IDropdownOption[] = ProductUtils.getFeatureOptions();
    const booleanOptions: any[] = DataUtils.getBooleanOptions2();

    const width6 = Utils.getColumnWidth(6);
    const width12 = Utils.getColumnWidth(12);

    const onSortChange = (event: React.FormEvent<HTMLDivElement>, item: any) => {
        let model = new SortModel();
        model.key = item.key;
        model.name = item.name;
        model.text = item.text;
        model.direction = item.direction;

        onValueChange("sortModel", model);
    }

    const sortPicker = () => {
        return <>
            <Dropdown
                label="Sort By"
                selectedKey={filter.sortModel.key}
                onChange={onSortChange}
                options={sortOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const deletedOnlyPicker = () => {
        return <>
            <Dropdown
                label="Deleted Only"
                selectedKey={filter.deletedOnly}
                onChange={(_, v) => onValueChange("deletedOnly", v?.key || 0)}
                options={booleanOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const activeOnlyPicker = () => {
        return <>
            <Dropdown
                label="Active Only"
                selectedKey={filter.activeOnly}
                onChange={(_, v) => onValueChange("activeOnly", v?.key || 0)}
                options={booleanOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const approvedOnlyPicker = () => {
        return <>
            <Dropdown
                label="Approved Only"
                selectedKey={filter.approvedOnly}
                onChange={(_, v) => onValueChange("approvedOnly", v?.key || 0)}
                options={booleanOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const inStockPicker = () => {
        return <>
            <Dropdown
                label="In Stock"
                selectedKey={filter.inStock}
                onChange={(_, v) => onValueChange("inStock", v?.key || 0)}
                options={booleanOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const featurePicker = () => {
        return <>
            <Dropdown
                label="Feature"
                selectedKey={filter.feature}
                onChange={(_, v) => onValueChange("feature", v?.key || 0)}
                options={featureOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const sizePicker = () => {
        return <>
            <Dropdown
                label="Size"
                selectedKey={filter.sizeId}
                onChange={(_, v) => onValueChange("sizeId", v?.key || 0)}
                options={sizeOptions}
                styles={dropdownStyles}
            />
        </>;
    }

    const levelPicker = () => {
        return <>
            <Dropdown
                label="Level"
                selectedKey={filter.level}
                onChange={(_, v) => onValueChange("level", v?.key || 0)}
                options={ProductUtils.getCustomerLevelOptions()}
                styles={dropdownStyles}
            />
        </>;
    }

    const noCategoryCheckbox = () => {
        return <>
            <Checkbox
                label="No Category"
                checked={filter.noCategory}
                styles={checkboxStyles}
                onChange={(_, v) => onValueChange("noCategory", v || false)} />
        </>;
    }

    const noPointCheckbox = () => {
        return <>
            <Checkbox
                label="No Earning Point"
                checked={filter.noEarningPoint}
                styles={checkboxStyles}
                onChange={(_, v) => onValueChange("noEarningPoint", v || false)}
            />
        </>;
    }

    const header = () => {
        return <div className="panel-header ms-Grid">
            <div className="ms-Grid-row ph-10">
                <div className={width6}>
                    <DefaultButton
                        className="default-button"
                        style={{ width: "100%" }}
                        onClick={onCancel}
                        styles={defaultButtonStyles}
                    >Cancel</DefaultButton>
                </div>
                <div className={width6}>
                    <PrimaryButton
                        onClick={onFilter}
                        styles={primaryButtonStyles}
                        style={{ width: "100%" }}
                    >Apply</PrimaryButton>
                </div>
            </div>
        </div>
    }

    return <div>
        <Panel
            isBlocking={true}
            isOpen={true}
            isLightDismiss={false}
            type={PanelType.custom}
            customWidth={DataUtils.PANEL_WIDTH}
            onRenderHeader={header}
            hasCloseButton={false}
            styles={{
                header: panelStyles.header,
                content: { paddingLeft: "10px !important", paddingRight: "10px !important" }
            }}
        >
            <div className="ms-Grid pv-10">
                <div className="ms-Grid-row">
                    <div className={width12}>
                        {sortPicker()}
                    </div>
                </div>

                <div className="ms-Grid-row pt-5">
                    <div className={width6}>{deletedOnlyPicker()}</div>
                    <div className={width6}>{activeOnlyPicker()}</div>
                </div>

                <div className="ms-Grid-row pt-5">
                    <div className={width6}>{approvedOnlyPicker()}</div>
                    <div className={width6}>{inStockPicker()}</div>
                </div>

                <div className="ms-Grid-row pt-5">
                    <div className={width12}>{levelPicker()}</div>
                </div>

                <div className="ms-Grid-row pt-5">
                    <div className={width6}>{featurePicker()}</div>
                    <div className={width6}>{sizePicker()}</div>
                </div>

                <div className="ms-Grid-row pt-15">
                    <div className={width6}>{noCategoryCheckbox()}</div>
                    <div className={width6}>{noPointCheckbox()}</div>
                </div>

                <div className="pt-20">
                    {getSelectedCategoryList().map((item: MenuModel, index: number) => {
                        return <>
                            <FilterButton
                                text={item.text}
                                onDelete={() => onCategoryChecked(item, false)}
                            />
                        </>;
                    })}
                </div>

                <div className="pt-15">
                    {categoryList.map((item: MenuModel, index: number) => {
                        return <>
                            <CategoryMenuItemView
                                item={item}
                                hideZeroCount={false}
                                onChecked={onCategoryChecked}
                                onCollapsed={onCategoryCollapsed}
                            />
                        </>
                    })}
                </div>
            </div>
        </Panel>
    </div>;
}